import { BadgeBlock } from "./blocks/BadgeBlock";
import { CalloutBlock } from "./blocks/CalloutBlock";
import { CardBlock } from "./blocks/CardBlock";
import { ChartBlock } from "./blocks/ChartBlock";
import { ChecklistItemBlock } from "./blocks/ChecklistItemBlock";
import { ExpandableBlock } from "./blocks/ExpandableBlock";
import { FilePreviewBlock } from "./blocks/FilePreviewBlock";
import { GridBlock } from "./blocks/GridBlock";
import { HeadingBlock } from "./blocks/HeadingBlock";
import { ListBlock } from "./blocks/ListBlock";
import { MetricBlock } from "./blocks/MetricBlock";
import { ParagraphBlock } from "./blocks/ParagraphBlock";
import { SectionBlock } from "./blocks/SectionBlock";
import { SourceLinkBlock } from "./blocks/SourceLinkBlock";
import { TableBlock } from "./blocks/TableBlock";
import { VideoPreviewBlock } from "./blocks/VideoPreviewBlock";
import { type CanvasBlock } from "./types";

interface CanvasBlockRendererProps {
  block: CanvasBlock;
  onSectionClick?: (sectionId: string, title: string) => void;
  onBadgeAction?: (action: string, label: string) => void;
}

export function CanvasBlockRenderer({
  block,
  onSectionClick,
  onBadgeAction,
}: CanvasBlockRendererProps) {
  switch (block.type) {
    case "heading":
      return <HeadingBlock level={block.level} text={block.text} />;

    case "paragraph":
      return <ParagraphBlock text={block.text} />;

    case "badge":
      return (
        <BadgeBlock
          data-testid="canvas-block-renderer-badge-block-39"
          label={block.label}
          color={block.color}
          size={block.size}
          onClick={
            block.action && onBadgeAction
              ? () => onBadgeAction(block.action!, block.label)
              : undefined
          }
        />
      );

    case "card":
      return (
        <CardBlock
          title={block.title}
          subtitle={block.subtitle}
          badge={block.badge}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        >
          {block.children}
        </CardBlock>
      );

    case "grid":
      return (
        <GridBlock
          columns={block.columns}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        >
          {block.children}
        </GridBlock>
      );

    case "table":
      return (
        <TableBlock
          headers={block.headers}
          rows={block.rows}
          highlightRules={block.highlightRules}
        />
      );

    case "list":
      return (
        <ListBlock
          ordered={block.ordered}
          items={block.items}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        />
      );

    case "divider":
      return <hr className="border-puntt-neutral-gray-6" />;

    case "metric":
      return (
        <MetricBlock
          label={block.label}
          value={block.value}
          trend={block.trend}
        />
      );

    case "expandable":
      return (
        <ExpandableBlock
          title={block.title}
          summary={block.summary}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        >
          {block.children}
        </ExpandableBlock>
      );

    case "section":
      return (
        <SectionBlock
          id={block.id}
          title={block.title}
          onSectionClick={onSectionClick}
          onBadgeAction={onBadgeAction}
        >
          {block.children}
        </SectionBlock>
      );

    case "source_link":
      return (
        <SourceLinkBlock
          title={block.title}
          url={block.url}
          linkType={block.linkType}
        />
      );

    case "chart":
      return <ChartBlock chartType={block.chartType} data={block.data} />;

    case "callout":
      return <CalloutBlock variant={block.variant} text={block.text} />;

    case "file_preview":
      return (
        <FilePreviewBlock
          url={block.url}
          fileName={block.fileName}
          fileType={block.fileType}
          pageCount={block.pageCount}
        />
      );

    case "video_preview":
      return <VideoPreviewBlock url={block.url} fileName={block.fileName} />;

    case "checklist_item":
      return (
        <ChecklistItemBlock
          criterion={block.criterion}
          status={block.status}
          finding={block.finding}
        />
      );

    default:
      return null;
  }
}
